import { Link } from "react-router-dom";
import shifaLogo from "@/assets/images/shifa-logo.png";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  ExternalLink,
} from "lucide-react";
import { siteConfig } from "@/data/site";
import { departments } from "@/data/departments";

const quickLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Departments", href: "/departments" },
  { label: "Services", href: "/services" },
  { label: "Our Doctors", href: "/doctors" },
  { label: "Contact Us", href: "/contact" },
  { label: "Facilities", href: "/facilities" },
  { label: "Exceptional Cases", href: "/rare-cases" },
  { label: "Book Appointment", href: "/appointment" },
  { label: "Services & Pricing", href: "/pricing" },
];

const HMS_PORTAL_URL =
  "http://192.168.1.4:805/HMS/GS_Reports/GS_OPD/GS_CollectionSummary.aspx#";

/** Internal HMS portals (reachable on the hospital network). */
const staffLinks = [
  { label: "Lab", href: HMS_PORTAL_URL },
  { label: "Reception", href: HMS_PORTAL_URL },
  { label: "IT", href: HMS_PORTAL_URL },
  { label: "Account", href: HMS_PORTAL_URL },
];

const socialLinks = [
  { icon: Facebook, href: siteConfig.social.facebook, label: "Facebook" },
  { icon: Instagram, href: siteConfig.social.instagram, label: "Instagram" },
  { icon: Twitter, href: siteConfig.social.twitter, label: "Twitter" },
  { icon: Youtube, href: siteConfig.social.youtube, label: "YouTube" },
];

export function Footer() {
  const displayDepartments = departments.slice(0, 8);

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main footer */}
      <div className="container-app py-12 lg:py-16">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-8">
          {/* Column 1: Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="mb-4 inline-flex items-center">
              <img src={shifaLogo} alt="Shifa Medical Centre" className="h-12 w-auto rounded bg-white p-1" />
            </Link>
            <p className="mb-4 text-sm leading-relaxed text-gray-400">
              {siteConfig.tagline}
            </p>
            <p className="text-sm leading-relaxed text-gray-500">
              A leading multi-specialty hospital in Srinagar, committed to
              compassionate, world-class healthcare with over{" "}
              {siteConfig.stats.years} years of dedicated service to the people
              of Kashmir and beyond.
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
              Quick Links
            </h3>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Departments */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
              Departments
            </h3>
            <ul className="space-y-2.5">
              {displayDepartments.map((dept) => (
                <li key={dept.id}>
                  <Link
                    to={`/departments/${dept.id}`}
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    {dept.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div id="contact-numbers" className="scroll-mt-32">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
              Contact Info
            </h3>
            <ul className="space-y-4">
              <li className="flex gap-3 text-sm">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-400" />
                <span className="text-gray-400">{siteConfig.address.full}</span>
              </li>
              {siteConfig.phoneGroups.map((group) => (
                <li key={group.label} className="flex gap-3 text-sm">
                  <Phone className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-400" />
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
                      {group.label}
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {group.numbers.map((n) => (
                        <li key={n.tel}>
                          <a
                            href={`tel:${n.tel}`}
                            className="text-gray-400 transition-colors hover:text-white"
                          >
                            {n.display}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              ))}
              <li>
                <a
                  href={`tel:${siteConfig.contact.emergency.replace(/-/g, "")}`}
                  className="flex items-center gap-3 text-sm text-red-400 transition-colors hover:text-red-300"
                >
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  Emergency: {siteConfig.contact.emergency}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${siteConfig.contact.email}`}
                  className="flex items-center gap-3 text-sm text-gray-400 transition-colors hover:text-white"
                >
                  <Mail className="h-4 w-4 flex-shrink-0 text-primary-400" />
                  {siteConfig.contact.email}
                </a>
              </li>
              <li className="flex gap-3 text-sm">
                <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-400" />
                <div className="text-gray-400">
                  <p>Mon - Sat: Consultation</p>
                  <p>Sunday: Emergency Only</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Column 5: Staff Portals */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
              Staff Portals
            </h3>
            <ul className="space-y-2.5">
              {staffLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    {link.label}
                    <ExternalLink className="h-3 w-3 text-gray-600" />
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs leading-relaxed text-gray-600">
              Accessible on the hospital network only.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="container-app flex flex-col items-center justify-between gap-4 py-5 sm:flex-row">
          <p className="text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Shifa Medical Centre. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy-policy"
              className="text-xs text-gray-500 transition-colors hover:text-gray-300"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-and-conditions"
              className="text-xs text-gray-500 transition-colors hover:text-gray-300"
            >
              Terms & Conditions
            </Link>
            <Link
              to="/refund-policy"
              className="text-xs text-gray-500 transition-colors hover:text-gray-300"
            >
              Refunds & Cancellations
            </Link>
            <Link
              to="/shipping-policy"
              className="text-xs text-gray-500 transition-colors hover:text-gray-300"
            >
              Shipping & Delivery
            </Link>
            <Link
              to="/waste-management"
              className="text-xs text-gray-500 transition-colors hover:text-gray-300"
            >
              Waste Management
            </Link>
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full p-1.5 text-gray-500 transition-colors hover:bg-gray-800 hover:text-white"
                  aria-label={social.label}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
